import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { translations, Language } from '../components/LanguageSelector/translations';
import NotificationToast from '../components/NotificationToast';
import CartAnimation from '../components/CartAnimation';

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  slug: string | null;
  isActive: boolean;
  translatedName?: string;
}

export default function ProductDetailsPage() {
  const { category, productSlug } = useParams<{ category: string; productSlug: string }>();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info'>('success');
  const [showCartAnimation, setShowCartAnimation] = useState(false);
  const { t, language } = useLanguage();
  const { user } = useAuth();

  const currentLanguage = translations[language as Language] || translations.en;

  useEffect(() => {
    if (category && productSlug) {
      fetchProduct();
    }
  }, [category, productSlug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${category}/${productSlug}`);
      setProduct(response.data);
    } catch (err) {
      setError('Product not found');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    if (!user) {
      setNotificationMessage(currentLanguage.products.loginRequired || 'Please login to add items to cart');
      setNotificationType('error');
      setShowNotification(true);
      return;
    }

    if (!product) return;

    if (quantity < 1) {
      setNotificationMessage('Please select a valid quantity');
      setNotificationType('error');
      setShowNotification(true);
      return;
    }

    try {
      setAddingToCart(true);
      await api.post('/cart/add', { productId: product.id, quantity });
      
      // Show success notification
      setNotificationMessage(`${quantity}x ${product.name} added to cart!`);
      setNotificationType('success');
      setShowNotification(true);
      
      // Start cart animation
      setShowCartAnimation(true);
      
      setQuantity(1); // Reset quantity after adding to cart
      
      // Emit cart update event
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || currentLanguage.products.errorAddingToCart || 'Error adding to cart';
      setNotificationMessage(errorMessage);
      setNotificationType('error');
      setShowNotification(true);
    } finally {
      setAddingToCart(false);
    }
  };

  const getCategoryLabel = (categorySlug: string | null) => {
    if (!categorySlug) return '';
    
    const categoryLabels: { [key: string]: string } = {
      'cheese': currentLanguage.products.categories?.cheese || 'Cheese',
      'wine': currentLanguage.products.categories?.wine || 'Wine',
      'olive-oil': currentLanguage.products.categories?.oliveOil || 'Olive Oil',
      'pasta': currentLanguage.products.categories?.pasta || 'Pasta',
      'salami': currentLanguage.products.categories?.salami || 'Salami',
      'honey': currentLanguage.products.categories?.honey || 'Honey',
      'truffle': currentLanguage.products.categories?.truffle || 'Truffle'
    };

    return categoryLabels[categorySlug] || categorySlug;
  };

  const getProductImages = (categorySlug: string | null, productSlug: string | null) => {
    if (!categorySlug || !productSlug) return [];
    
    // For wine category, return wine images (small versions for carousel)
    if (categorySlug === 'wine') {
      return [
        `/products/${categorySlug}/${productSlug}/${categorySlug}_1_small.jpg`,
        `/products/${categorySlug}/${productSlug}/${categorySlug}_2_small.jpg`
      ];
    }
    
    return [];
  };

  const getProductImagesForModal = (categorySlug: string | null, productSlug: string | null) => {
    if (!categorySlug || !productSlug) return [];
    
    // For wine category, return medium versions for modal
    if (categorySlug === 'wine') {
      return [
        `/products/${categorySlug}/${productSlug}/${categorySlug}_1_medium.jpg`,
        `/products/${categorySlug}/${productSlug}/${categorySlug}_2_medium.jpg`
      ];
    }
    
    return [];
  };

  const getProductDescription = (productName: string, category: string | null) => {
    // Map product names to translation keys
    const productMap: { [key: string]: string } = {
      'Montepulciano d\'Abruzzo DOC': 'products.wine.montepulciano.description',
      'Trebbiano d\'Abruzzo DOC': 'products.wine.trebbiano.description',
      'Pecorino Romano DOP': 'products.cheese.pecorino.description',
      'Caciocavallo Silano': 'products.cheese.caciocavallo.description',
      'Olio Extra Vergine di Oliva': 'products.oliveOil.extraVergine.description',
      'Pasta alla Chitarra': 'products.pasta.chitarra.description',
      'Lenticchie di Santo Stefano': 'products.pasta.lenticchie.description',
      'Salsiccia di Fegato': 'products.salami.salsiccia.description',
      'Miele di Castagno': 'products.honey.castagno.description',
      'Tartufo Nero': 'products.truffle.nero.description'
    };

    const translationKey = productMap[productName];
    if (translationKey) {
      // Use a safer approach to access nested translations
      try {
        const keys = translationKey.split('.');
        let translation: any = currentLanguage;
        for (const key of keys) {
          translation = translation?.[key];
          if (!translation) break;
        }
        return translation || '';
      } catch (error) {
        console.warn(`Translation not found for key: ${translationKey}`);
        return '';
      }
    }
    return '';
  };


  const getImageName = (imagePath: string) => {
    return imagePath.split('/').pop()?.split('.')[0] || 'Image';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{currentLanguage.common.loading || 'Loading...'}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{currentLanguage.common.productNotFound || 'Product Not Found'}</h1>
            <p className="text-gray-600 mb-8">{currentLanguage.common.productNotFoundDescription || 'The product you are looking for does not exist.'}</p>
            <Link 
              to="/products" 
              className="bg-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-700 transition-colors"
            >
              {currentLanguage.common.backToHome || 'Back to Products'}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const productImages = getProductImages(product.category.slug, product.slug);
  const productImagesForModal = getProductImagesForModal(product.category.slug, product.slug);
  const translatedName = product.name;
  const translatedDescription = getProductDescription(product.name, product.category.slug);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      {/* Hero Section with Banner Image */}
              <div className="relative h-96 bg-cover bg-center" style={{ backgroundImage: `url('/products/${product.category.slug}/${product.slug}/${product.category.slug}_banner.jpg')` }}>
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-5xl font-bold mb-2">{translatedName}</h1>
            <p className="text-xl">
              {getCategoryLabel(product.category.slug)}
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-gray-500 hover:text-green-600">{currentLanguage.common.home || 'Home'}</Link>
            <span className="text-gray-400">/</span>
            <Link to="/products" className="text-gray-500 hover:text-green-600">{t('tripDetails.breadcrumbProducts')}</Link>
            <span className="text-gray-400">/</span>
            <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              {translatedName}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product Description and Gallery */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Description and Images */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              {/* Description */}
              <h3 className="text-xl font-semibold mb-4">{currentLanguage.common.overview || 'Overview'}</h3>
              <p className="text-gray-700 leading-relaxed mb-8">
                {translatedDescription}
              </p>

              {/* Gallery */}
              <h3 className="text-xl font-semibold mb-4">{currentLanguage.common.gallery || 'Gallery'}</h3>
              <div className="relative">
                {/* Left Arrow */}
                <button 
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-md transition-all duration-200"
                  onClick={() => {
                    const slider = document.getElementById('product-image-slider');
                    if (slider) {
                      slider.scrollBy({ left: -200, behavior: 'smooth' });
                    }
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Right Arrow */}
                <button 
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-md transition-all duration-200"
                  onClick={() => {
                    const slider = document.getElementById('product-image-slider');
                    if (slider) {
                      slider.scrollBy({ left: 200, behavior: 'smooth' });
                    }
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <div 
                  id="product-image-slider"
                  className="flex space-x-4 overflow-x-auto pb-4 px-8" 
                  style={{ 
                    scrollbarWidth: 'none', 
                    msOverflowStyle: 'none',
                    ...({ WebkitScrollbar: { display: 'none' } } as any)
                  }}
                >
                  {productImages.map((imagePath: string, index: number) => (
                    <div 
                      key={index}
                      className="flex-shrink-0 cursor-pointer group"
                      onClick={() => {
                        setSelectedImageIndex(index);
                        setIsModalOpen(true);
                      }}
                    >
                      <img
                        src={imagePath}
                        alt={`${translatedName} - ${getImageName(imagePath)}`}
                        className="w-32 h-24 object-cover rounded-lg shadow-md transition-transform duration-200 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Creative Commons Attribution */}
              <div className="mt-4">
                <p className="text-left text-xs text-gray-500">
                  <span>Images with Creative Commons license — </span>
                  <a 
                    href="/commons.wikimedia.info" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-600 underline"
                  >
                    full credits here.
                  </a>
                </p>
              </div>
            </div>

            {/* Product Details */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-4">{currentLanguage.common.details || 'Product Details'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{currentLanguage.products.categories?.wine || 'Category'}</h4>
                  <p className="text-gray-600">{getCategoryLabel(product.category.slug)}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{currentLanguage.common.price || 'Price'}</h4>
                  <p className="text-gray-600">€{product.price.toFixed(2)}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{currentLanguage.products.inStock || 'Availability'}</h4>
                  <p className="text-gray-600">{product.stock} {currentLanguage.common.items || 'items'} {currentLanguage.products.inStock || 'in stock'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{currentLanguage.common.region || 'Region'}</h4>
                  <p className="text-gray-600">Abruzzo, Italy</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{currentLanguage.common.origin || 'Origin'}</h4>
                  <p className="text-gray-600">Montepulciano d'Abruzzo DOC</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{currentLanguage.common.volume || 'Volume'}</h4>
                  <p className="text-gray-600">750ml</p>
                </div>
              </div>
            </div>
          </div>

          {/* Purchase Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-6 shadow-sm sticky top-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{translatedName}</h3>
              
              {/* Price and Stock */}
              <div className="flex items-center gap-4 mb-6">
                <span className="text-3xl font-bold text-green-600">
                  €{product.price.toFixed(2)}
                </span>
                <span className={`text-sm px-3 py-1 rounded-full ${
                  product.stock > 10 
                    ? 'bg-green-100 text-green-800' 
                    : product.stock > 0 
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {product.stock > 0 
                    ? `${currentLanguage.products.inStock || 'In stock'}: ${product.stock}`
                    : currentLanguage.products.outOfStock || 'Out of stock'
                  }
                </span>
              </div>

              {/* Product Info */}
              <div className="mb-6">
                <p className="text-gray-600 text-sm mb-2">
                  <span className="font-semibold">{getCategoryLabel(product.category.slug)}</span>
                </p>
                <p className="text-gray-600 text-sm">
                  {product.stock} {currentLanguage.common.items || 'items'} {currentLanguage.products.inStock || 'in stock'}
                </p>
              </div>

              {/* Quantity Selector */}
              {user && product.stock > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {(currentLanguage.products as any).quantity || 'Quantity'}:
                  </label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                      disabled={quantity <= 1}
                      className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={product.stock}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                      className="w-16 h-10 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => setQuantity(prev => Math.min(product.stock, prev + 1))}
                      disabled={quantity >= product.stock}
                      className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {(currentLanguage.products as any).maxQuantity || 'Max'}: {product.stock}
                  </p>
                </div>
              )}

              {/* Add to Cart Button */}
              <button
                onClick={addToCart}
                disabled={!user || product.stock === 0 || addingToCart}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-colors duration-200 ${
                  !user || product.stock === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : addingToCart
                    ? 'bg-green-400 text-white cursor-wait'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {!user 
                  ? (currentLanguage.products.loginToAdd || 'Login to add')
                  : product.stock === 0
                  ? (currentLanguage.products.outOfStock || 'Out of stock')
                  : addingToCart
                  ? (currentLanguage.products.adding || 'Adding...')
                  : (currentLanguage.products.addToCart || 'Add to Cart')
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Left Arrow */}
            {productImagesForModal.length > 1 && (
              <button 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-3 rounded-full shadow-md transition-all duration-200"
                onClick={() => {
                  setSelectedImageIndex(prev => 
                    prev === 0 ? productImagesForModal.length - 1 : prev - 1
                  );
                }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Right Arrow */}
            {productImagesForModal.length > 1 && (
              <button 
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-3 rounded-full shadow-md transition-all duration-200"
                onClick={() => {
                  setSelectedImageIndex(prev => 
                    prev === productImagesForModal.length - 1 ? 0 : prev + 1
                  );
                }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Image */}
            <img
              src={productImagesForModal[selectedImageIndex]}
              alt={`${translatedName} - ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />

            {/* Image Counter */}
            {productImagesForModal.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                {selectedImageIndex + 1} / {productImagesForModal.length}
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
      
      {/* Notification Toast */}
      <NotificationToast
        message={notificationMessage}
        type={notificationType}
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        duration={4000}
      />
      
      {/* Cart Animation */}
      <CartAnimation
        isVisible={showCartAnimation}
        onComplete={() => setShowCartAnimation(false)}
      />
    </div>
  );
}
